import { CompositeChart } from "@mantine/charts";
import { EmptyState } from "#/components/ui/EmptyState";
import type { PrvBucket, TimelinePoint } from "./prvUtils";
import { usePrvTranslation } from "./usePrvTranslation";

interface Props {
  points: TimelinePoint[];
  /** The client's rotation target expressed per bucket; drawn as a dashed line, never judged. */
  target: number | null;
  bucket: PrvBucket;
}

/**
 * Arrivals rise above the axis, departures hang below it, and the line is how
 * many items were at the sub-client (in a stay) at the end of each bucket:
 * stays started minus stays ended. All three are item counts, so they share
 * one axis.
 */
const PrvTimelineChart = ({ points, target, bucket }: Props) => {
  const t = usePrvTranslation();

  if (points.length === 0) {
    return (
      <EmptyState
        title={t("clients.prv.results.timeline.empty")}
        description={t("clients.prv.results.timeline.emptyHint")}
      />
    );
  }

  return (
    <CompositeChart
      h={300}
      data={points}
      dataKey="label"
      withLegend
      legendProps={{ verticalAlign: "bottom", height: 32 }}
      tickLine="y"
      gridAxis="y"
      curveType="monotone"
      maxBarWidth={bucket === "week" ? 18 : 28}
      barProps={{ radius: 2 }}
      strokeWidth={2}
      withDots={points.length <= 24}
      valueFormatter={(value) => String(Math.abs(value))}
      yAxisProps={{ tickFormatter: (value: number) => String(Math.abs(value)) }}
      series={[
        {
          name: "arrivals",
          label: t("clients.prv.results.timeline.arrivals"),
          color: "blue.6",
          type: "bar",
        },
        {
          name: "departures",
          label: t("clients.prv.results.timeline.departures"),
          color: "orange.6",
          type: "bar",
        },
        {
          name: "staying",
          label: t(
            bucket === "week"
              ? "clients.prv.results.timeline.onSiteWeek"
              : "clients.prv.results.timeline.onSiteMonth"
          ),
          color: "teal.7",
          type: "line",
        },
      ]}
      referenceLines={
        target !== null
          ? [
              {
                y: target,
                label: t("clients.prv.results.timeline.target", { value: target.toFixed(1) }),
                color: "gray.6",
                strokeDasharray: "4 4",
              },
            ]
          : []
      }
    />
  );
};

export default PrvTimelineChart;
